// Honest result stages (SPEC 3.13, 7.3): accepted, handed to the agent, agent continued; or not sure,
// changed, expired, refused. Announced through aria-live. There is never a resend button.
import type { SendRecord, SendStage } from "../lib/model";
import { IconAlert, IconCheck, IconQuestion, IconActivity } from "./Icons";

export const CHECKING_AFTER_MS = 8000;
export const CHECKING_ACCEPTED_AFTER_MS = 15_000;

/** The last result for an answer to a Codex card whose turn had ended (daemon reason "turn-ended"). */
export const TURN_ENDED_DONE = "Done. Ask Codex to continue at your desk.";

export type ResultTone = "ok" | "warn" | "bad" | "neutral";

export function describeResult(rec: SendRecord, now: number): { tone: ResultTone; headline: string; detail?: string; checking: boolean } {
  const age = now - rec.updatedAt;
  switch (rec.stage) {
    case "sending":
      return age >= CHECKING_AFTER_MS ? { tone: "warn", headline: "Not sure it arrived. Checking.", detail: `No word from ${rec.machineName} yet. Nothing is resent.`, checking: true } : { tone: "neutral", headline: "Sent.", detail: `Waiting for ${rec.machineName} to accept it.`, checking: false };
    case "accepted":
      return age >= CHECKING_ACCEPTED_AFTER_MS
        ? { tone: "warn", headline: "Accepted, but not handed over yet. Checking.", detail: `${rec.machineName} has it. Nothing is resent.`, checking: true }
        : { tone: "neutral", headline: `Accepted by ${rec.machineName}.`, detail: rec.turnEnded ? `Handing it to ${rec.agentName}. Then ask Codex to continue at your desk.` : `Handing it to ${rec.agentName}.`, checking: false };
    case "handed":
      return { tone: "neutral", headline: `Handed to ${rec.agentName}.`, detail: rec.turnEnded ? "Codex will run it. Ask Codex to continue at your desk." : "Waiting to see the agent move on.", checking: false };
    case "continued":
      // Codex had ended its turn, so it ran the action but won't move on by itself: "continued" would be untrue.
      if (rec.turnEnded) return { tone: "ok", headline: TURN_ENDED_DONE, detail: "Codex had finished its turn, so it won't see the result until you do.", checking: false };
      return { tone: "ok", headline: `Answered. ${rec.agentName} continued.`, checking: false };
    case "unknown":
      return { tone: "warn", headline: "Not sure it arrived.", detail: rec.reason ?? `${rec.machineName} couldn't confirm the outcome. Check the agent's state before answering again; nothing is resent.`, checking: false };
    case "refused":
      return { tone: "bad", headline: refusedHeadline(rec.reason), detail: rec.reason, checking: false };
    case "expired":
      return { tone: "warn", headline: "No longer waiting.", detail: rec.reason, checking: false };
    case "changed":
      return { tone: "warn", headline: "Not sent. The question changed.", detail: "The new question needs a fresh tap.", checking: false };
    case "not-sent":
      return { tone: "warn", headline: rec.reason ?? "Not sent.", detail: "Nothing left your phone. Tap again.", checking: false };
  }
}

function refusedHeadline(reason?: string): string {
  if (reason && /already answered/i.test(reason)) return reason;
  return "Refused by your PC.";
}

const STEPS: { stage: SendStage; word: string }[] = [
  { stage: "accepted", word: "Accepted" },
  { stage: "handed", word: "Handed over" },
  { stage: "continued", word: "Continued" },
];
const ORDER: SendStage[] = ["sending", "accepted", "handed", "continued"];

export function ResultView({ rec, now }: { rec: SendRecord; now: number }) {
  const d = describeResult(rec, now);
  const idx = ORDER.indexOf(rec.stage);
  const showSteps = idx >= 0;
  return (
    <section className={`result ${d.tone}`} role="status" aria-live="polite" aria-atomic="true">
      <div className="stage">
        {d.tone === "ok" ? <IconCheck /> : d.tone === "bad" ? <IconAlert /> : d.tone === "warn" ? <IconQuestion /> : <IconActivity />}
        <span>
          {rec.summary} · {d.headline}
        </span>
      </div>
      {showSteps && (
        <div className="steps" aria-label="Progress">
          {STEPS.map((s, i) => {
            const si = ORDER.indexOf(s.stage);
            const cls = si < idx ? "on" : si === idx ? (d.checking ? "" : "now") : "";
            return (
              <span key={s.stage} className={cls}>
                {i > 0 && <i aria-hidden="true" />}
                {s.stage === "continued" && rec.turnEnded ? "Done" : s.word}
              </span>
            );
          })}
        </div>
      )}
      {d.detail && <p>{d.detail}</p>}
    </section>
  );
}
